import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { TOOLBAR_BUTTON } from '@atlaskit/editor-common/ui-menu';

import { EditorToolbarLoomButtonSpotlight } from '@confluence/loom-onboarding/entry-points/EditorToolbarLoomButtonSpotlight.ts';
import { useLoomEntryPointErrorCallback } from '@confluence/loom-utils/entry-points/useLoomEntryPointErrorCallback.ts';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant.ts';
import {
	CrossFlowMenuItem,
	CrossJoinMenuItem,
} from '@confluence/loom-utils/entry-points/LoomMenuItem.tsx';
import { LoomCrossJoinPopupCard } from '@confluence/loom-utils/entry-points/LoomCrossJoinPopupCard.ts';
import { LoomCrossFlowPopupCard } from '@confluence/loom-utils/entry-points/LoomCrossFlowPopupCard.ts';
import { DiscoverabilityPulse } from '@confluence/loom-utils/entry-points/DiscoverabilityPulse.ts';
import type { EditorLoomButtonProps } from '@confluence/loom-editor-utils/entry-points/loomEditorUtils-types.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

const ENTRYPOINT_ID = 'loom-confluence-page-editor-toolbar';
const SOURCE_COMPONENT = 'loomConfluencePageEditorToolbar';
const SOURCE_CONTEXT = 'confluencePageEditorToolbarRecordAVideo';

const i18n = defineMessages({
	crossSellPopupCardDescription: {
		id: 'editor-presets.full-page-editor.crossSellPopupCardDescription',
		defaultMessage: 'Save time and engage your team with a video walkthrough of this page.',
		description: 'Description inside a popup card advertising an opportunity to try Loom',
	},
	crossSellPopupCardTitle: {
		id: 'editor-presets.full-page-editor.crossSellPopupCardTitle',
		defaultMessage: 'Share your work with a video',
		description: 'Title of a popup card advertising an opportunity to try Loom',
	},
});

const EditorLoomButtonComponent = ({
	buttonComponent: ButtonComponent,
	editorAPIRef,
	flags,
}: EditorLoomButtonProps) => {
	const isNewToolbarEnabled =
		(fg('aifc_create_enabled') ||
			expVal('platform_editor_toolbar_aifc_confluence', 'isEnabled', false)) &&
		expVal('platform_editor_toolbar_migrate_loom', 'isEnabled', false);
	const intl = useIntl();
	const { entryPointVariant, error, loading } = useLoomEntryPointVariant();

	const loomEntryPointErrorCallback = useLoomEntryPointErrorCallback({ flags });

	if (entryPointVariant === 'BETA' || (isNewToolbarEnabled && entryPointVariant === 'CO_USE')) {
		// Fall back to the plugin's default behavior when in a recorder-eligible state
		return <ButtonComponent />;
	}

	if (error) {
		return <ButtonComponent onClickBeforeInit={loomEntryPointErrorCallback} />;
	}

	if (loading) {
		return <ButtonComponent isDisabled />;
	}

	if (entryPointVariant === 'CROSS_FLOW') {
		if (isNewToolbarEnabled) {
			return (
				<CrossFlowMenuItem
					buttonComponent={ButtonComponent}
					sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
					entryPointId={ENTRYPOINT_ID}
					sourceComponent={SOURCE_COMPONENT}
					sourceContext={SOURCE_CONTEXT}
				/>
			);
		}

		return (
			<LoomCrossFlowPopupCard
				description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
				entryPointId={ENTRYPOINT_ID}
				placement="bottom-end"
				// Render in a Portal to isolate from conflicts caused by editor-based CSS + keyboard & focus behavior
				shouldRenderToParent={false}
				sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
				sourceComponent={SOURCE_COMPONENT}
				sourceContext={SOURCE_CONTEXT}
				title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
				trigger={({ isSelected, onClick, ...otherTriggerProps }) => {
					return (
						<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID}>
							<ButtonComponent
								onClickBeforeInit={onClick}
								selected={isSelected}
								{...otherTriggerProps}
							/>
						</DiscoverabilityPulse>
					);
				}}
			/>
		);
	}

	if (entryPointVariant === 'CROSS_JOIN') {
		if (isNewToolbarEnabled) {
			return (
				<CrossJoinMenuItem
					buttonComponent={ButtonComponent}
					sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
					entryPointId={ENTRYPOINT_ID}
					flags={fg('twcg-1241_streamlining_loom_cross_join_gate') ? flags : undefined}
				/>
			);
		}

		return (
			<LoomCrossJoinPopupCard
				description={intl.formatMessage(i18n.crossSellPopupCardDescription)}
				entryPointId={ENTRYPOINT_ID}
				placement="bottom-end"
				// Render in a Portal to isolate from conflicts caused by editor-based CSS + keyboard & focus behavior
				shouldRenderToParent={false}
				sourceActionSubjectId={TOOLBAR_BUTTON.RECORD_VIDEO}
				title={intl.formatMessage(i18n.crossSellPopupCardTitle)}
				triggerLink={(href, { onClick, ...otherTriggerProps }) => (
					<DiscoverabilityPulse entryPointId={ENTRYPOINT_ID}>
						<ButtonComponent
							{...otherTriggerProps}
							href={href}
							onClickBeforeInit={onClick}
							rel="noreferrer"
							target="_blank"
						/>
					</DiscoverabilityPulse>
				)}
				flags={fg('twcg-1241_streamlining_loom_cross_join_gate') ? flags : undefined}
			/>
		);
	}

	if (entryPointVariant === 'CO_USE') {
		return (
			<EditorToolbarLoomButtonSpotlight
				editorAPIRef={editorAPIRef}
				spotlightPlacement="bottom right"
				nudgeSource="fullPageEditorToolbar"
				epMessage="cc-loom-onboarding-spotlight-editor"
			>
				<ButtonComponent />
			</EditorToolbarLoomButtonSpotlight>
		);
	}

	return null;
};

export const EditorLoomButton = withFlags(EditorLoomButtonComponent);
